import { useEffect, useMemo, useState } from "react"
import { useAuth } from "contexts/auth-context"
import { getBranchList } from "helpers/general-helper"

import CustomerPickerModal from "./customer-picker-modal"

const initialState = {
  customer_id: "",
  customer_name: "",
  customer_phone: "",
  outlet_id: "",
  type: "CREDIT",
  source_type: "MANUAL",
  source_id: "",
  amount: "",
  issue_date: "",
  expiry_date: "",
  description: "",
}

const parseRupiahInput = (value) =>
  String(value ?? "").replace(/[^\d]/g, "")

const formatRupiahInput = (value) => {
  const digits = parseRupiahInput(value)
  if (!digits) return ""
  return new Intl.NumberFormat("id-ID").format(Number(digits))
}

const formatDateTimeLocalValue = (value) => {
  if (!value) return ""

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const pad = (num) => String(num).padStart(2, "0")

  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-") + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const todayDateValue = () => {
  const date = new Date()
  const pad = (num) => String(num).padStart(2, "0")

  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-")
}

const withDefaultTime = (value, time) => {
  if (!value) return ""

  return `${String(value).slice(0, 10)}T${time}`
}

export default function FormModal({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  defaultOutletId = "",
  initialData = null,
}) {
  const { currentBranch } = useAuth()
  const [form, setForm] = useState(initialState)
  const [openCustomerPicker, setOpenCustomerPicker] = useState(false)
  const isEditMode = Boolean(initialData)

  const branchSelection = getBranchList()

  const outletOptions = useMemo(() => {
    if (!Array.isArray(branchSelection)) return []
    return branchSelection
      .filter((item) => item?.id)
      .map((item) => ({
        value: String(item.id),
        label: item.name || `Outlet ${item.id}`,
        raw: item,
      }))
  }, [branchSelection])

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          customer_id: String(initialData.customer_id || ""),
          customer_name: initialData.customer_name || "",
          customer_phone: initialData.customer_phone || "",
          outlet_id: String(initialData.outlet_id || defaultOutletId || currentBranch?.id || ""),
          type: initialData.type || "CREDIT",
          source_type: initialData.source_type || "MANUAL",
          source_id: initialData.source_id || "",
          amount: formatRupiahInput(initialData.original_amount || ""),
          issue_date: formatDateTimeLocalValue(initialData.issue_date),
          expiry_date: formatDateTimeLocalValue(initialData.expiry_date),
          description: initialData.description || "",
        })
        return
      }

      setForm({
        ...initialState,
        outlet_id: String(defaultOutletId || currentBranch?.id || ""),
        issue_date: withDefaultTime(todayDateValue(), "00:00"),
        expiry_date: withDefaultTime(todayDateValue(), "23:59"),
      })
    }
  }, [open, defaultOutletId, currentBranch, initialData])

  useEffect(() => {
    if (String(form.type || "").toUpperCase() === "DEBIT" && form.expiry_date) {
      setForm((prev) => ({
        ...prev,
        expiry_date: "",
      }))
    }
  }, [form.type, form.expiry_date])

  if (!open) return null

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handlePickCustomer = (customer) => {
    setForm((prev) => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_phone: customer.phone,
    }))
    setOpenCustomerPicker(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const amountValue = Number(parseRupiahInput(form.amount) || 0)
    const issueDateValue = form.issue_date ? new Date(form.issue_date) : null
    const expiryDateValue =
      String(form.type || "").toUpperCase() === "CREDIT" && form.expiry_date
        ? new Date(form.expiry_date)
        : null

    if (amountValue <= 0) {
      window.alert("Nominal amount harus lebih dari 0.")
      return
    }

    if (
      issueDateValue &&
      expiryDateValue &&
      expiryDateValue < issueDateValue
    ) {
      window.alert("Tanggal expired tidak boleh lebih kecil dari tanggal issue.")
      return
    }

    if (isEditMode) {
      await onSubmit({
        description: form.description || null,
      })
      return
    }

    await onSubmit({
      customer_id: form.customer_id,
      customer_name: form.customer_name || null,
      customer_phone: form.customer_phone || null,
      outlet_id: Number(form.outlet_id),
      type: form.type,
      source_type: form.source_type || "MANUAL",
      source_id: form.source_id || null,
      amount: amountValue,
      issue_date: form.issue_date || null,
      expiry_date:
        String(form.type || "").toUpperCase() === "CREDIT"
          ? (form.expiry_date || null)
          : null,
      description: form.description || null,
    })
  }

  // CSS Class standar untuk input agar seragam dan rapi
  const inputClassName = "w-full px-4 py-2.5 text-sm bg-white border rounded-lg border-neutral-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200"

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        {/* Container Utama dengan max-h-[90vh] agar tidak melebihi tinggi layar */}
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
          
          <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">
            {/* HEADER - Fixed di atas */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  {initialData ? "Edit Customer Note" : "Tambah Customer Note"}
                </h3>
                <p className="text-sm text-neutral-500 mt-0.5">
                  {initialData
                    ? "Perbarui Credit Note atau Debit Note customer"
                    : "Input Credit Note atau Debit Note customer"}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-full transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* BODY - Scrollable area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {isEditMode ? (
                <div className="px-4 py-3 text-sm border rounded-xl border-amber-200 bg-amber-50 text-amber-800">
                  Pada mode edit, hanya kolom keterangan yang bisa diubah.
                </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                    Type
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => updateField("type", e.target.value)}
                    className={inputClassName}
                    disabled={isEditMode}
                  >
                    <option value="CREDIT">CREDIT</option>
                    <option value="DEBIT">DEBIT</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                    Outlet <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.outlet_id}
                    onChange={(e) => updateField("outlet_id", e.target.value)}
                    className={inputClassName}
                    required
                    disabled={isEditMode}
                  >
                    <option value="">Pilih outlet</option>
                    {outletOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2 p-4 border border-neutral-200 rounded-xl bg-neutral-50/50">
                  <label className="block mb-2 text-sm font-medium text-neutral-700">
                    Customer <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => setOpenCustomerPicker(true)}
                      disabled={isEditMode}
                      className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition shadow-sm whitespace-nowrap"
                    >
                      Pilih Customer
                    </button>

                    <div className="flex-1 px-4 py-2.5 border rounded-lg border-neutral-300 bg-white min-h-[42px] flex items-center">
                      {form.customer_id ? (
                        <div className="flex flex-col">
                          <span className="font-semibold text-neutral-800 text-sm">
                            {form.customer_name || "-"}
                          </span>
                          <span className="text-xs text-neutral-500">
                            ID: {form.customer_id}
                            {form.customer_phone ? ` • ${form.customer_phone}` : ""}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-400 italic">
                          Belum ada customer yang dipilih
                        </span>
                      )}
                    </div>
                  </div>
                  {!form.customer_id && (
                    <p className="mt-2 text-xs text-red-500">
                      Customer wajib dipilih untuk melanjutkan.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">Rp</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.amount}
                      onChange={(e) =>
                        updateField("amount", formatRupiahInput(e.target.value))
                      }
                      className={`${inputClassName} pl-10`}
                      placeholder="0"
                      required
                      disabled={isEditMode}
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                    Source Type
                  </label>
                  <input
                    type="text"
                    value={form.source_type}
                    onChange={(e) => updateField("source_type", e.target.value)}
                    className={inputClassName}
                    placeholder="MANUAL / RETURN / EXCHANGE"
                    disabled={isEditMode}
                  />
                </div>

                <div>
                  <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                    Issue Date
                  </label>
                  <input
                    type="datetime-local"
                    value={form.issue_date}
                    onChange={(e) =>
                      updateField("issue_date", withDefaultTime(e.target.value, "00:00"))
                    }
                    className={inputClassName}
                    disabled={isEditMode}
                  />
                </div>

                {String(form.type || "").toUpperCase() === "CREDIT" ? (
                  <div>
                    <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                      Expiry Date
                    </label>
                    <input
                      type="datetime-local"
                      value={form.expiry_date}
                      onChange={(e) =>
                        updateField("expiry_date", withDefaultTime(e.target.value, "23:59"))
                      }
                      className={inputClassName}
                      disabled={isEditMode}
                    />
                  </div>
                ) : null}

                <div className="md:col-span-2">
                  <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                    Source ID
                  </label>
                  <input
                    type="text"
                    value={form.source_id}
                    onChange={(e) => updateField("source_id", e.target.value)}
                    className={inputClassName}
                    placeholder="Contoh: INV-2023-10-001 (Opsional)"
                    disabled={isEditMode}
                  />
                </div>
              </div>

              <hr className="border-neutral-100" />

              <div>
                <label className="block mb-1.5 text-sm font-medium text-neutral-700">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  className={inputClassName}
                  placeholder="Tulis deskripsi note di sini..."
                />
              </div>
            </div>

            {/* FOOTER - Fixed di bawah */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50 shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-5 py-2.5 text-sm font-medium transition bg-white border rounded-lg border-neutral-300 text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 disabled:opacity-50"
              >
                Batal
              </button>

              <button
                type="submit"
                disabled={isSubmitting || !form.customer_id || !form.outlet_id}
                className="px-5 py-2.5 text-sm font-medium text-white transition bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {isSubmitting ? "Menyimpan..." : initialData ? "Update Note" : "Simpan Note"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <CustomerPickerModal
        open={openCustomerPicker}
        onClose={() => setOpenCustomerPicker(false)}
        onSelect={handlePickCustomer}
      />
    </>
  )
}
